import React, { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Group, Radio, Stack, Text, Textarea } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import * as yup from 'yup'

import { getNextClosingStep } from './closingNavigation'
import {
  clearAttendeeTranscriptOrders,
  gateConfirmOrders,
  resolveClosingSyncEffect,
  undoTranscriptOrderActions,
} from './closingOrders'
import { NoShowOffRecordVerbatim } from './NoShowOffRecordVerbatim'
import {
  TranscriptOrderCollectionContent,
  TranscriptOrderConfirmationContent,
} from './TranscriptOrderActions'

import { CNAVerbatim } from '@/components/Event/NewScript/components/Closing/CNAVerbatim'
import { EventResultOptions } from '@/components/Event/NewScript/components/common/EventResultOptions'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { ActionTitle } from '@/components/Event/NewScript/components/common/ScriptTimeline'
import { ActionButton } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionButton'
import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { ActionText } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionText'
import { ContinueButton } from '@/components/Event/NewScript/components/common/ScriptTimeline/ContinueButton'
import { ScriptTimeline } from '@/components/Event/NewScript/components/common/ScriptTimeline/ScriptTimeline'
import { TimelineAction } from '@/components/Event/NewScript/components/common/ScriptTimeline/TimelineAction'
import { OpeningVerbatim } from '@/components/Event/NewScript/components/Opening/OpeningVerbatim'
import { NORMAL_CLOSING_ACTIONS } from '@/components/Event/NewScript/hooks/state/stateActions'
import { ScriptActions } from '@/components/Event/NewScript/hooks/state/useScriptActions'
import { useStepActions } from '@/components/Event/NewScript/hooks/state/useStepActions'
import { useEndZoomEvent } from '@/components/Event/NewScript/hooks/useEndZoomEvent'
import { useStatePolicies } from '@/components/Event/NewScript/hooks/useStatePolicy'
import { EVENT_FORM_CONTEXT_IS_FINAL_SCRIPT_EVENT } from '@/components/Event/NewScript/schemas'
import { canceledClosingEventSchema } from '@/components/Event/NewScript/schemas/canceledEventSchema'
import { EVENT_FORM_CONTEXT_IS_EUO } from '@/components/Event/NewScript/schemas/formContext'
import {
  getWitnessEventTitle,
  isExaminationUnderOath,
} from '@/components/Event/NewScript/utils/event'
import { getBaseStepKey } from '@/components/Event/NewScript/utils/stepKey'
import {
  AttendeeRole,
  DepositionResult,
  REASON_FOR_CNA_LABEL,
  ReasonForCna,
  ScriptStep,
  TimestampType,
} from '@/constants'
import { useReporter } from '@/providers/UserProvider'
import { Event, EventScriptDataObject } from '@/types'
import { getCurrentTimeInUtc, getEventTimezone } from '@/utils/time'

type EventCancelledInterface = yup.InferType<typeof canceledClosingEventSchema>

type EventCancelledClosingFormProps = {
  step: string
  event: Event
  state: EventScriptDataObject
  actions: ScriptActions
  first_skipped_step: string
  currentStepActions: any
  currentEventIndex: number | undefined
  witnessIndex: number | undefined
  scriptEventCount: number
}

const CNA_BASE_CLOSING_ACTIONS = [
  { step: ScriptStep.CLOSING, key: 'state_the_cna' },
  { step: ScriptStep.CLOSING, key: 'select_reason_for_cna' },
]
const CNA_TRANSCRIPT_ORDER_ACTION_KEYS = [
  'collect_transcript_orders',
  'confirm_orders',
]
const CNA_CLOSING_ACTIONS = [
  ...CNA_BASE_CLOSING_ACTIONS,
  ...CNA_TRANSCRIPT_ORDER_ACTION_KEYS.map(key => ({
    step: ScriptStep.CLOSING,
    key,
  })),
]

// The transcript-order actions are the only normal closing actions this form
// renders. Everything else in the normal flow is off screen here, so the step
// must not wait on it: a cancelled proceeding skips those actions, but the
// backend re-applies its own skip rules on every fetch, and an action that is
// neither skipped nor completed nor on screen leaves the reporter unable to
// leave the step.
const HIDDEN_NORMAL_CLOSING_ACTIONS: string[] = NORMAL_CLOSING_ACTIONS.filter(
  key => !CNA_TRANSCRIPT_ORDER_ACTION_KEYS.includes(key),
)

export const EventCancelledClosingForm: React.FC<
  EventCancelledClosingFormProps
> = ({
  event,
  step,
  state,
  actions,
  first_skipped_step,
  currentStepActions,
  currentEventIndex,
  witnessIndex,
  scriptEventCount,
}) => {
  const reporter = useReporter()
  const isEUO = isExaminationUnderOath(event)
  const cnaTranscriptOrdersEnabled =
    event.cna_transcript_orders_enabled && !isEUO
  const cnaClosingActions = cnaTranscriptOrdersEnabled
    ? CNA_CLOSING_ACTIONS
    : CNA_BASE_CLOSING_ACTIONS

  const {
    completeAction,
    isActionCompleted,
    canContinue,
    completeStepAndContinue,
    undoAction,
  } = useStepActions(step, event, {
    hiddenSatisfiedActions: cnaTranscriptOrdersEnabled
      ? HIDDEN_NORMAL_CLOSING_ACTIONS
      : [...HIDDEN_NORMAL_CLOSING_ACTIONS, ...CNA_TRANSCRIPT_ORDER_ACTION_KEYS],
  })
  const {
    isAK,
    isAR,
    isAZ,
    isDE,
    isDC,
    isIL,
    isKY,
    isME,
    isND,
    isNE,
    isNM,
    isNY,
    isPA,
    isNV,
    isMS,
    isMT,
    isRI,
    isSD,
    isTN,
    isUT,
    isVA,
    isVT,
    isWV,
    isWY,
  } = useStatePolicies(event)
  const witness = state.attendees.find(
    attendee => attendee.role === AttendeeRole.WITNESS,
  )
  const firstSkippedBaseStep = getBaseStepKey(first_skipped_step)
  const { confirmAndEndEvent, isLoading } = useEndZoomEvent(event)
  const nextStep = getNextClosingStep({
    eventCount: scriptEventCount,
    witnessIndex,
  })
  const isFinalScriptEvent = nextStep === ScriptStep.REVIEW

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(canceledClosingEventSchema),
    context: {
      [EVENT_FORM_CONTEXT_IS_FINAL_SCRIPT_EVENT]: isFinalScriptEvent,
      [EVENT_FORM_CONTEXT_IS_EUO]: isEUO || !cnaTranscriptOrdersEnabled,
    },
    defaultValues: {
      result: state.result,
      notes: state.notes,
      reason_for_cna: state.reason_for_cna ?? undefined,
      cna_declared_at: state.cna_declared_at,
      last_timestamp: state.last_timestamp,
      no_show_is_on_record: state.is_on_record,
      no_show_is_off_record: state.is_on_record,
      other_reason: state.other_reason,
      order_transcript: state.order_transcript,
      attendees: state.attendees,
      attendees_have_left_call: isActionCompleted('attendees_have_left_call'),
    },
  })

  const { register, control } = methods
  const selectedResult = methods.watch('result')

  const onContinueClick = () => {
    if (nextStep === ScriptStep.REVIEW) {
      confirmAndEndEvent(() => completeStepAndContinue(nextStep))
    } else {
      completeStepAndContinue(nextStep)
    }
  }

  useEffect(() => {
    if (state.result !== DepositionResult.CNA) {
      return
    }

    actions.unskipStepAction({
      currentEventIndex,
      result: state.result,
      notes: state.notes,
      reasonForCNA: state.reason_for_cna,
      otherReason: state.other_reason,
      stepAndActionKeys: cnaClosingActions,
    })
    // Ensure saved CNA scripts expose the CNA closing actions when reopened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      const closingEffect = resolveClosingSyncEffect(name, value)
      if (closingEffect?.type === 'clearTranscriptOrders') {
        const clearedAttendees = clearAttendeeTranscriptOrders(
          methods.getValues('attendees'),
        )
        undoTranscriptOrderActions(isActionCompleted, undoAction)
        actions.updateOrderTranscript(false)
        actions.updateAttendees({
          attendees: clearedAttendees,
        })
        methods.setValue('order_transcript', false)
        methods.setValue('attendees', clearedAttendees)
      } else if (
        cnaTranscriptOrdersEnabled &&
        closingEffect?.type === 'orderTranscript'
      ) {
        actions.updateOrderTranscript(closingEffect.value)
      } else if (
        cnaTranscriptOrdersEnabled &&
        closingEffect?.type === 'attendees'
      ) {
        actions.updateAttendees({ attendees: closingEffect.attendees })
      }

      if (
        name &&
        ['reason_for_cna', 'other_reason', 'notes', 'result'].includes(name)
      ) {
        if (value.result === DepositionResult.CNA) {
          actions.unskipStepAction({
            currentEventIndex,
            result: value.result,
            notes: value.notes,
            reasonForCNA: value.reason_for_cna,
            otherReason: value.other_reason,
            stepAndActionKeys: cnaClosingActions,
          })
        } else {
          actions.skipStepAction({
            currentEventIndex,
            result: value.result,
            notes: value.notes,
            reasonForCNA: value.reason_for_cna,
            otherReason: value.other_reason,
            stepAndActionKeys: CNA_CLOSING_ACTIONS,
          })
        }

        if (value.result === DepositionResult.COMPLETED_NO_SHOW) {
          actions.unskipStepAction({
            currentEventIndex,
            result: value.result,
            notes: value.notes,
            reasonForCNA: value.reason_for_cna,
            otherReason: value.other_reason,
            stepAndActionKeys: [
              {
                step: ScriptStep.CLOSING,
                key: 'no_show_go_on_the_record',
              },
              {
                step: ScriptStep.CLOSING,
                key: 'no_show_script',
              },
            ],
          })
        } else {
          actions.skipStepAction({
            currentEventIndex,
            result: value.result,
            notes: value.notes,
            reasonForCNA: value.reason_for_cna,
            otherReason: value.other_reason,
            stepAndActionKeys: [
              {
                step: ScriptStep.CLOSING,
                key: 'no_show_go_on_the_record',
              },
              {
                step: ScriptStep.CLOSING,
                key: 'no_show_script',
              },
            ],
          })
        }
      }
    })

    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch, state, actions])

  const validateAndCompleteAction = async (
    validation_keys: Array<keyof EventCancelledInterface>,
    action_key: string,
  ) => {
    const results: Promise<boolean>[] = []

    validation_keys.forEach(key => {
      results.push(methods.trigger(key))
    })

    Promise.all(results).then(res => {
      res.every(isValid => isValid) && completeAction(action_key)
    })
  }
  const completeAttendeesHaveLeftCallAction = async () => {
    methods.setValue('attendees_have_left_call', true, {
      shouldValidate: true,
    })
    const isValid = await methods.trigger('attendees_have_left_call')

    if (isValid) {
      completeAction('attendees_have_left_call')
    }
  }
  const hasWitnessIndex = witnessIndex !== undefined
  const witnessName = hasWitnessIndex
    ? state.attendees.find(a => a.role === AttendeeRole.WITNESS)?.name
    : undefined

  return (
    <Stack>
      <ScriptTimeline
        title={
          witnessIndex === undefined
            ? '5. Closing'
            : `${witnessIndex + 2}.3 Closing - ${getWitnessEventTitle(event, witnessName)}`
        }
      >
        {/* Declare how deposition ended */}
        <TimelineAction
          actionKey="declare_how_deposition_ended"
          title="Declare how deposition ended"
          onComplete={() =>
            validateAndCompleteAction(
              ['result'],
              'declare_how_deposition_ended',
            )
          }
          completed={isActionCompleted('declare_how_deposition_ended')}
        >
          <ActionContainer outer>
            <Stack>
              <ActionContainer>
                <EventResultOptions
                  control={control}
                  canceledFromStep={firstSkippedBaseStep}
                  event={event!}
                />
              </ActionContainer>
              {methods.formState.errors.result !== undefined && (
                <FormErrors errors={methods.formState.errors.result.message} />
              )}
              {firstSkippedBaseStep !== ScriptStep.ATTENDANCE && (
                <ActionContainer>
                  <Textarea
                    {...register('notes')}
                    label="Notes"
                    placeholder="How was the event? (Anything worth mentioning?)"
                    minRows={3}
                  />
                </ActionContainer>
              )}
            </Stack>
          </ActionContainer>
        </TimelineAction>
        {!currentStepActions.state_the_cna.skip && (
          <TimelineAction
            actionKey="state_the_cna"
            title="State the CNA on the record"
            onComplete={() =>
              validateAndCompleteAction(['cna_declared_at'], 'state_the_cna')
            }
            completed={isActionCompleted('state_the_cna')}
          >
            <ActionContainer outer>
              <Stack>
                <ActionContainer>
                  <Group>
                    <ActionButton
                      onClick={() =>
                        actions.addTimestamp({
                          isOnRecord: true,
                          timestampType: TimestampType.RECORDING_ON,
                          timezone: getEventTimezone(event),
                        })
                      }
                      disabled={state.is_on_record}
                    >
                      Go on record
                    </ActionButton>
                    {state.is_on_record && (
                      <Text weight={700} size="xs">
                        You are now on the record.
                      </Text>
                    )}
                  </Group>
                </ActionContainer>
                <ActionText mono>
                  <CNAVerbatim
                    eventDatetime={event.datetime}
                    timezone={getEventTimezone(event)}
                    eventName={event.name}
                    witnessName={witness?.name ?? "[State Witness' name]]"}
                  />
                </ActionText>
                <ActionContainer>
                  <Group>
                    <ActionButton
                      onClick={() => {
                        actions.addTimestamp({
                          isOnRecord: false,
                          timestampType: TimestampType.RECORDING_OFF,
                          timezone: getEventTimezone(event),
                          isCnaDeclaration: true,
                        })
                        const currentTime = getCurrentTimeInUtc()
                        methods.setValue('cna_declared_at', currentTime)
                        methods.setValue('last_timestamp', currentTime)
                      }}
                      disabled={!state.is_on_record}
                    >
                      Go off record
                    </ActionButton>
                    {!state.is_on_record && (
                      <Text weight={700} size="xs">
                        You are off the record.
                      </Text>
                    )}
                  </Group>
                </ActionContainer>
                <FormErrors
                  errors={methods.formState.errors?.cna_declared_at?.message}
                />
              </Stack>
            </ActionContainer>
          </TimelineAction>
        )}
        {!currentStepActions.select_reason_for_cna.skip && (
          <TimelineAction
            actionKey="select_reason_for_cna"
            title="Select reason for CNA"
            completed={isActionCompleted('select_reason_for_cna')}
            onComplete={() =>
              validateAndCompleteAction(
                ['reason_for_cna', 'other_reason'],
                'select_reason_for_cna',
              )
            }
          >
            <ActionContainer outer>
              <Stack>
                <ActionContainer>
                  <Controller
                    name="reason_for_cna"
                    control={control}
                    render={({ field }) => (
                      <Radio.Group
                        {...field}
                        onChange={value => {
                          field.onChange(value as ReasonForCna)
                        }}
                      >
                        <Stack>
                          {Object.values(ReasonForCna).map(value => (
                            <Radio
                              styles={{ radio: { cursor: 'pointer' } }}
                              key={value}
                              value={value}
                              label={REASON_FOR_CNA_LABEL[value]}
                            />
                          ))}
                        </Stack>
                      </Radio.Group>
                    )}
                  />
                </ActionContainer>
                {methods.formState.errors.reason_for_cna !== undefined && (
                  <FormErrors
                    errors={methods.formState.errors.reason_for_cna.message}
                  />
                )}
                <ActionContainer>
                  <Textarea
                    {...register('other_reason')}
                    label="Specify the other reason (will be visible on the certificate)"
                    placeholder="How was the event? (Anything worth mentioning?)"
                    minRows={3}
                  />
                </ActionContainer>
                {methods.formState.errors.other_reason !== undefined && (
                  <FormErrors
                    errors={methods.formState.errors.other_reason.message}
                  />
                )}
              </Stack>
            </ActionContainer>
          </TimelineAction>
        )}
        {cnaTranscriptOrdersEnabled &&
          selectedResult === DepositionResult.CNA && (
            <TimelineAction
              actionKey="collect_transcript_orders"
              title="Collect transcript orders"
              onComplete={() =>
                validateAndCompleteAction(
                  ['order_transcript', 'attendees'],
                  'collect_transcript_orders',
                )
              }
              completed={isActionCompleted('collect_transcript_orders')}
            >
              <FormProvider {...methods}>
                <TranscriptOrderCollectionContent
                  event={event}
                  state={state}
                  isActionCompleted={isActionCompleted}
                  undoAction={undoAction}
                />
              </FormProvider>
            </TimelineAction>
          )}
        {cnaTranscriptOrdersEnabled &&
          selectedResult === DepositionResult.CNA && (
            <TimelineAction
              actionKey="confirm_orders"
              title="Confirm transcript orders with attendees"
              onComplete={() => {
                const gate = gateConfirmOrders(
                  isActionCompleted('collect_transcript_orders'),
                )
                if (!gate.allowed) {
                  showNotification(gate.notification)
                  return
                }
                validateAndCompleteAction(['attendees'], 'confirm_orders')
              }}
              completed={isActionCompleted('confirm_orders')}
            >
              <FormProvider {...methods}>
                <TranscriptOrderConfirmationContent state={state} />
              </FormProvider>
            </TimelineAction>
          )}
        {!currentStepActions.no_show_go_on_the_record.skip && (
          <TimelineAction
            actionKey="no_show_go_on_the_record"
            title="Go on the record"
            onComplete={() =>
              validateAndCompleteAction(
                ['no_show_is_on_record'],
                'no_show_go_on_the_record',
              )
            }
            completed={isActionCompleted('no_show_go_on_the_record')}
          >
            <ActionContainer outer>
              <Stack>
                <ActionText mono>
                  Is everyone ready to go on the record?
                </ActionText>
                <ActionContainer>
                  <Group>
                    <ActionButton
                      onClick={() => {
                        actions.addTimestamp({
                          isOnRecord: true,
                          timestampType: TimestampType.RECORDING_ON,
                          timezone: getEventTimezone(event),
                        })
                        methods.setValue('no_show_is_on_record', true, {
                          shouldValidate: true,
                        })
                      }}
                      disabled={state.is_on_record}
                    >
                      Go on record
                    </ActionButton>
                    {state.is_on_record && (
                      <Text weight={700} size="xs">
                        You are now on the record.
                      </Text>
                    )}
                  </Group>
                </ActionContainer>
                <FormErrors
                  errors={
                    methods.formState.errors?.no_show_is_on_record?.message
                  }
                />
              </Stack>
            </ActionContainer>
            <ActionContainer outer>
              <Stack>
                <ActionContainer>
                  <ActionTitle>Reminder</ActionTitle>
                  <ActionText>
                    If needed, please as the counsel for the case number.
                  </ActionText>
                </ActionContainer>
              </Stack>
            </ActionContainer>
            <ActionContainer outer>
              <Stack>
                <ActionText mono>
                  <OpeningVerbatim
                    event={event}
                    isAK={isAK}
                    isAR={isAR}
                    isAZ={isAZ}
                    isDE={isDE}
                    isDC={isDC}
                    isIL={isIL}
                    isKY={isKY}
                    isME={isME}
                    isND={isND}
                    isNE={isNE}
                    isNM={isNM}
                    isNY={isNY}
                    isPA={isPA}
                    isNV={isNV}
                    isMS={isMS}
                    isMT={isMT}
                    isRI={isRI}
                    isSD={isSD}
                    isTN={isTN}
                    isUT={isUT}
                    isVA={isVA}
                    isVT={isVT}
                    isWV={isWV}
                    isWY={isWY}
                    reporter={reporter}
                    triggerProceedCopy
                  />
                </ActionText>
              </Stack>
            </ActionContainer>
          </TimelineAction>
        )}
        {!currentStepActions.no_show_script.skip && (
          <TimelineAction
            actionKey="no_show_script"
            title="Allow counsel to read their witness no show script"
            onComplete={() =>
              validateAndCompleteAction(
                ['no_show_is_off_record'],
                'no_show_script',
              )
            }
            completed={isActionCompleted('no_show_script')}
          >
            <ActionContainer outer>
              <Stack>
                <ActionContainer>
                  <Group>
                    <ActionButton
                      onClick={() => {
                        actions.addTimestamp({
                          isOnRecord: false,
                          timestampType: TimestampType.RECORDING_OFF,
                          timezone: getEventTimezone(event),
                          isCnaDeclaration: true,
                        })
                        methods.setValue('no_show_is_off_record', true, {
                          shouldValidate: true,
                        })
                      }}
                      disabled={!state.is_on_record}
                    >
                      Go off record
                    </ActionButton>
                    {!state.is_on_record && (
                      <Text weight={700} size="xs">
                        You are off the record.
                      </Text>
                    )}
                  </Group>
                </ActionContainer>
                <FormErrors
                  errors={
                    methods.formState.errors?.no_show_is_off_record?.message
                  }
                />
              </Stack>
            </ActionContainer>
            <ActionContainer outer>
              <Stack>
                <ActionText mono>
                  <NoShowOffRecordVerbatim timezone={getEventTimezone(event)} />
                </ActionText>
              </Stack>
            </ActionContainer>
          </TimelineAction>
        )}
        {isFinalScriptEvent && (
          <TimelineAction
            actionKey="attendees_have_left_call"
            title="Confirm all attendees have left the call"
            onComplete={() => completeAttendeesHaveLeftCallAction()}
            completed={isActionCompleted('attendees_have_left_call')}
            collapsible={false}
          />
        )}
        <ContinueButton
          event={event}
          label={
            nextStep === ScriptStep.REVIEW
              ? 'Continue to review'
              : 'Continue to next witness'
          }
          canContinue={canContinue()}
          step={ScriptStep.CLOSING}
          loading={isLoading}
          handleClick={() => onContinueClick()}
          endsZoomCallOnContinue={nextStep === ScriptStep.REVIEW}
          reminder={
            isFinalScriptEvent && !isActionCompleted('attendees_have_left_call')
              ? '(Please wait to move on until all attendees have left the call.)'
              : undefined
          }
        />
      </ScriptTimeline>
    </Stack>
  )
}
